<script setup lang="ts">
    import { computed, onMounted, watch, shallowReactive, Ref } from 'vue'
    import { useRoute } from 'vue-router'
    import { EditorState } from '@common/Common'
    import { guid } from '@common/Guid'
    import CorporateNavigation from '@common/Components/CorporateNavigation.vue'
    import RevenueEditor from '@Quest/Revenues/Components/RevenueEditor.vue'
    import type { RevenueEditorFormSubmitEventPayload } from '@Quest/Revenues/Components/RevenueEditor'
    import { RevenueEditorViewModel } from '@Quest/Revenues/Components/RevenueEditor'
    import type { RevenueAccountReadModel, RevenueAccountsReadModel } from '@Quest/Revenues/readmodels'
    import { useRevenueAccountsByStrategyIdQueryV1 } from '@Quest/Revenues/queries-vue-composables'
    import { useCreateRevenueAccountCommandV1, useUpdateRevenueAccountCommandV1, useDeleteRevenueAccountCommandV1 } from '@Quest/Revenues/commands-vue-composables'

    const route = useRoute()

    const workspaceId = computed(() => Number(route.params.workspaceId ?? 0))
    const strategyId = computed(() => String(route.params.strategyId ?? ''))

    const revenueEditor = shallowReactive(new RevenueEditorViewModel())

    const {
        runRevenueAccountsByStrategyIdQueryV1,
        modelRevenueAccountsByStrategyIdQueryV1
    } = useRevenueAccountsByStrategyIdQueryV1(workspaceId, strategyId)

    const rm = modelRevenueAccountsByStrategyIdQueryV1 as Ref<RevenueAccountsReadModel | null>

    const {
        invokeCreateRevenueAccountCommandV1,
        succeedCreateRevenueAccountCommandV1
    } = useCreateRevenueAccountCommandV1(workspaceId, strategyId)

    const {
        invokeUpdateRevenueAccountCommandV1,
        succeedUpdateRevenueAccountCommandV1
    } = useUpdateRevenueAccountCommandV1(workspaceId, strategyId)

    const {
        invokeDeleteRevenueAccountCommandV1,
        succeedDeleteRevenueAccountCommandV1
    } = useDeleteRevenueAccountCommandV1(workspaceId, strategyId)

    watch(rm, (model) =>
    {
        if (!model) return
        revenueEditor.close()
    })

    watch(
        [
            succeedCreateRevenueAccountCommandV1,
            succeedUpdateRevenueAccountCommandV1,
            succeedDeleteRevenueAccountCommandV1
        ],
        ([create, update, del], [prevCreate, prevUpdate, prevDel]) =>
        {
            if (
                (create && !prevCreate) ||
                (update && !prevUpdate) ||
                (del && !prevDel)
            )
            {
                runRevenueAccountsByStrategyIdQueryV1()
            }
        }
    )

    function showRevenueCreator()
    {
        revenueEditor.showCreator()
    }

    function showRevenueEditor(r: RevenueAccountReadModel)
    {
        revenueEditor.showEditor(r.Id, r.Name, r.Description)
    }

    function handleRevenueEditorFormSubmit(e: RevenueEditorFormSubmitEventPayload)
    {
        if (revenueEditor.state === EditorState.Creator)
        {
            invokeCreateRevenueAccountCommandV1({
                id: guid(),
                name: e.name,
                description: e.description
            })
        }
        else if (revenueEditor.state === EditorState.Editor)
        {
            invokeUpdateRevenueAccountCommandV1({
                id: e.id,
                name: e.name,
                description: e.description
            })
        }
    }

    function handleDeleteRevenueAccountCommand(rev: RevenueAccountReadModel)
    {
        if (confirm("Are you sure?"))
        {
            invokeDeleteRevenueAccountCommandV1({
                id: rev.Id
            })
        }
    }

    onMounted(() =>
    {
        document.title = "Revenues | TransparentChoice"
        runRevenueAccountsByStrategyIdQueryV1()
    })
</script>

<template>
    <div>
        <RevenueEditor :viewModel="revenueEditor" 
                       @form-submit="handleRevenueEditorFormSubmit" />
        
        <CorporateNavigation ActivePage="strategy" />
        
        <div class="container" v-if="rm" style="margin-top: 80px; padding-top: 40px;">
            <div class="row">
                <div class="col-lg-6">
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">Revenues</h1>
                </div>
                <div class="col-lg-6 text-right align-self-end">
                    <button role="button" class="btn btn-success" style="padding: 0.7em 2em;" @click="showRevenueCreator">Create revenue</button>
                </div>
            </div>
            <hr style="border-color: #bbb; margin: 20px 0 60px;" />
            <div class="mt-3">
                <table class="table table-bordered table-hover">
                    <thead>
                        <tr>
                            <th>Revenue</th>
                            
                            <th style="width: 120px;"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="rev in rm.Accounts" :key="rev.Id">
                            <td>
                                <strong>{{rev.Name}}</strong><br />{{rev.Description}}
                            </td>
                            <td>
                                <div class="dropdown">
                                    <button type="button" class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown">
                                        Actions
                                    </button>
                                    <div class="dropdown-menu dropdown-menu-right">
                                        <a class="dropdown-item" @click="showRevenueEditor(rev)">Edit</a>
                                        <div class="dropdown-divider"></div>
                                        <a class="dropdown-item" @click="handleDeleteRevenueAccountCommand(rev)">Delete</a>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>